import { CalendarPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { track } from '@/lib/track'

type AddToCalendarProps = {
  eventId: string
  eventSlug: string
  title: string
  date: string
  startTime: string | null
  endTime: string | null
  venue: string | null
  description: string | null
}

function formatDateToken(date: string) {
  return date.replace(/-/g, '')
}

function addDays(date: string, days: number) {
  const nextDate = new Date(`${date}T00:00:00Z`)
  nextDate.setUTCDate(nextDate.getUTCDate() + days)

  return nextDate.toISOString().slice(0, 10)
}

function buildGoogleCalendarUrl({
  title,
  date,
  startTime,
  endTime,
  venue,
  description,
}: Omit<AddToCalendarProps, 'eventId' | 'eventSlug'>) {
  const params = new URLSearchParams()
  params.set('action', 'TEMPLATE')
  params.set('text', title)

  const dateToken = formatDateToken(date)

  if (startTime) {
    const start = new Date(startTime)
    const startFormatted = `${dateToken}T${start.getUTCHours().toString().padStart(2, '0')}${start.getUTCMinutes().toString().padStart(2, '0')}00`

    if (endTime) {
      const end = new Date(endTime)
      const endFormatted = `${dateToken}T${end.getUTCHours().toString().padStart(2, '0')}${end.getUTCMinutes().toString().padStart(2, '0')}00`
      params.set('dates', `${startFormatted}/${endFormatted}`)
    } else {
      params.set('dates', `${startFormatted}/${startFormatted}`)
    }
  } else {
    params.set('dates', `${dateToken}/${formatDateToken(addDays(date, 1))}`)
  }

  if (venue) params.set('location', venue)
  if (description) params.set('details', description.slice(0, 500))

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export default function AddToCalendar({
  eventId,
  eventSlug,
  title,
  date,
  startTime,
  endTime,
  venue,
  description,
}: AddToCalendarProps) {
  const googleUrl = buildGoogleCalendarUrl({ title, date, startTime, endTime, venue, description })
  const icsUrl = `/events/${eventSlug}/calendar`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <CalendarPlus className="mr-2 size-4" />
          Add to Calendar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onClick={() => {
            track(eventId, 'calendar_save', { type: 'google' })
            window.open(googleUrl, '_blank')
          }}
        >
          Google Calendar
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            track(eventId, 'calendar_save', { type: 'ics' })
            window.location.href = icsUrl
          }}
        >
          Download .ics
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
